export type ModelFileExtension = 'glb' | 'gltf' | 'obj' | 'stl'

export type BabylonModelFile = {
    name?: string
    url?: string
    type?: string
    content_type?: string
    contentType?: string
    filename?: string
    original_filename?: string
    mime?: string
}

export const normalizeContentType = (contentType?: string) =>
    (contentType || '').split(';')[0].trim().toLowerCase()

export const contentTypeToModelExtension = (contentType?: string): ModelFileExtension | '' => {
    const rawContentType = normalizeContentType(contentType)
    if (!rawContentType) return ''

    if (
        rawContentType === 'model/gltf-binary' ||
        rawContentType === 'model/gltf.binary' ||
        rawContentType === 'application/glb' ||
        rawContentType === 'application/x-glb'
    ) {
        return 'glb'
    }

    if (
        rawContentType === 'model/gltf+json' ||
        rawContentType === 'model/gltf-json' ||
        rawContentType === 'model/gltf' ||
        rawContentType === 'application/gltf+json'
    ) {
        return 'gltf'
    }

    if (rawContentType === 'model/obj') return 'obj'
    if (rawContentType === 'model/stl' || rawContentType === 'application/sla') return 'stl'

    return ''
}

export const modelMimeTypeForExtension = (extension?: string) => {
    switch (extension) {
        case 'glb':
            return 'model/gltf-binary'
        case 'gltf':
            return 'model/gltf+json'
        case 'obj':
            return 'model/obj'
        case 'stl':
            return 'model/stl'
        default:
            return 'application/octet-stream'
    }
}

export const extensionFromValue = (value?: string) => {
    if (!value) return ''
    const withoutQuery = String(value).split(/[?#]/)[0]
    const lastSegment = withoutQuery.split('/').pop() || withoutQuery
    const match = lastSegment.toLowerCase().match(/\.([a-z0-9]+)$/)
    return match?.[1] || ''
}

export const filenameFromContentDisposition = (disposition?: string | null) => {
    if (!disposition) return ''

    const encodedMatch = disposition.match(/filename\*\s*=\s*(?:UTF-8''|)([^;]+)/i)
    const quotedMatch = disposition.match(/filename\s*=\s*"([^"]+)"/i)
    const plainMatch = disposition.match(/filename\s*=\s*([^;]+)/i)
    const rawValue = encodedMatch?.[1] || quotedMatch?.[1] || plainMatch?.[1]
    if (!rawValue) return ''

    const trimmed = rawValue.trim().replace(/^"(.*)"$/, '$1')
    try {
        return decodeURIComponent(trimmed.replace(/\+/g, '%20'))
    } catch {
        return trimmed
    }
}

export const modelHintsFromUrl = (url?: string) => {
    const query = String(url || '')
        .split('?')[1]
        ?.split('#')[0]
    if (!query) {
        return { extensionFromContentType: '', extensionFromDisposition: '' }
    }

    const params = new URLSearchParams(query)
    const responseContentType = params.get('response-content-type')
    const responseDisposition = params.get('response-content-disposition')

    return {
        extensionFromContentType: contentTypeToModelExtension(responseContentType || undefined),
        extensionFromDisposition: extensionFromValue(
            filenameFromContentDisposition(responseDisposition),
        ),
    }
}

export const modelExtensionFromArrayBuffer = (
    arrayBuffer: ArrayBuffer,
): ModelFileExtension | '' => {
    if (arrayBuffer.byteLength < 4) return ''

    const bytes = new Uint8Array(arrayBuffer, 0, Math.min(arrayBuffer.byteLength, 96))
    if (bytes[0] === 0x67 && bytes[1] === 0x6c && bytes[2] === 0x54 && bytes[3] === 0x46) {
        return 'glb'
    }

    try {
        const head = new TextDecoder('utf-8').decode(bytes).trimStart().toLowerCase()
        if (head.startsWith('{')) return 'gltf'
        if (head.startsWith('solid ')) return 'stl'
        if (head.startsWith('v ') || head.includes('\nv ')) return 'obj'
    } catch {
        return ''
    }

    return ''
}

export const modelLoadConfig = (file: BabylonModelFile) => {
    const extensionFromContentType = contentTypeToModelExtension(
        file.content_type || file.contentType || file.mime || file.type,
    )
    const urlHints = modelHintsFromUrl(file.url)
    const fileExtension =
        extensionFromContentType ||
        extensionFromValue(file.name) ||
        extensionFromValue(file.filename) ||
        extensionFromValue(file.original_filename) ||
        urlHints.extensionFromContentType ||
        urlHints.extensionFromDisposition ||
        extensionFromValue(file.url)
    const pluginExtension = ['glb', 'gltf', 'obj', 'stl'].includes(fileExtension)
        ? `.${fileExtension}`
        : undefined

    return {
        isGLB: fileExtension === 'glb',
        fileExtension,
        pluginExtension,
    }
}
